import { inject, Injectable, signal } from '@angular/core';
import * as auth from 'aws-amplify/auth';
import { fetchAuthSession } from 'aws-amplify/auth';
import { NotificationService } from './notifications-service';
import {AppLogUtils} from '../utils/log-utils';
import { AmplifyService } from './amplify.service';
import { Utils } from '../utils/amplify-utils';
import { SIGN_IN_STATUS, UserProfile } from '../utils/commonTypes';
import { API_ENDPOINT } from '../utils/api/api-service';
import { userApiSchema } from '../utils/api/api-types/api-schema';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private user: UserProfile|null=null;
  public currentUser = signal(<UserProfile>{id:'',status:'',name:'',sub:'',email:'',avatar:'',about:'',phone:'',groups:[]});
  private notifService = inject(NotificationService);

  private amplifyService = inject(AmplifyService);
  private amplifyClient = this.amplifyService.getClient();

  public getUserProfile(): UserProfile|null {
    return this.user;
  }

  private initOk = false;

  public async init(): Promise<boolean>{
      if(!this.initOk){
        AppLogUtils.info('APP USER INIT');
        this.initOk=true;
        //Reads data from the id token and make it available for the application
        // await this.extractUserProfileFromToken();

        //Start (or renew) existing subscriptions
        // this.notifService.refreshNotificationSubscriptions();

        return true;
      }else{
        return false;
      }
  }

  public async signIn(name:string,password:string):Promise<SIGN_IN_STATUS> {
    const result = await auth.signIn({username:name,password:password});
    if(result.isSignedIn){
      // await this.extractUserProfileFromToken();
    }else{
      if(result.nextStep.signInStep == 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE'){
        return SIGN_IN_STATUS.CONFIRM_EMAIL;
      }else{
        //TODO handle other cases
        return SIGN_IN_STATUS.FAILURE;
      }
    }
    return SIGN_IN_STATUS.FAILURE;
  }

  public async signOut(){
    const result = await auth.signOut();
    this.user=null;
  }

  public async updateUser(props:{userId:string,name?:string,avatarUrl?:string,about?:string,phone?:string}):Promise<boolean>{
    const updateResult = await this.amplifyClient.models.User.update(
      {id: props.userId, 
        name: props.name?props.name:undefined,
        avatar: props.avatarUrl?props.avatarUrl:undefined,
        about: props.about?props.about:undefined,
        phone: props.phone?props.phone:undefined
      
      });
    const userProfile = Utils.getDataFromResult(updateResult.data, updateResult.errors, 'User.update');
    this.forceTokenRefresh();
    return userProfile!=null;
  }

  public async loadUser(id:string): Promise<UserProfile|null>{
    const result = await this.amplifyClient.models.User.get({id:id});
    const user = Utils.getDataFromResult(result.data, result.errors, 'User.get');
    if(!user){return null;}
    return {
      id: user.id,
      name: user.name,
      sub: user.sub,
      email: user.email,
      avatar: user.avatar,
      about: user.about,
      phone: user.phone,
    } as UserProfile;
  }


  /**
   * Retrieves user profile, attributes, and groups from the ID token.
   * If not possible, the user profile will be null
   */
  private async extractUserProfileFromToken(): Promise<UserProfile|null>{
    try {
      // Fetch the current auth session
      const session = await fetchAuthSession();

      // Extract the ID token
      const idToken = session.tokens?.idToken?.toString();
      if (!idToken) {
        throw new Error('Could not extract ID token.');
      }

      // Decode the token payload
      const decoded = JSON.parse(atob(idToken.split('.')[1]));

      // Extract attributes based on UserProfile interface
      const profile: UserProfile = {
        id: decoded['userId'],                 // Custom claim user ID
        sub: decoded['sub'],                   // Cognito user ID (sub)
        name: decoded['userName'] || '',       // Custom claim for user name
        email: decoded['email'],               // Email attribute
        avatar: decoded['avatar'] || '',       // Custom claim for avatar
        groups: decoded['cognito:groups'] || [], // Cognito groups
        about:'',
        phone:'',
        status:'',
      };

      // Log claims for debugging
      console.log('SET => User Profile:', profile);

      // Return the user profile
      this.user =  profile;
      
      //Trigger signals
      this.currentUser.set(profile);
      
      return this.user;

      // this.store.dispatch(UserActions.userProfileLoaded({up:profile}));
    } catch (error) {
      console.log(`Could not extract extract user data from the token or renew subscriptions => ${error}`);
      return null;
    }
  }

  /**
   * In case the user is added/removed from a group, or the profile
   * changes, we need to refresh the auth token by updating a cognito 
   * attribute (whatever attribute).
   */
  public async forceTokenRefresh() {
    AppLogUtils.info(`Refresh user token`);

      // Generate a timestamp for the attribute update
      const timestamp = new Date().toISOString();

      // Update a dummy attribute to trigger token refresh
      await auth.updateUserAttributes({
        userAttributes: {
          'nickname': timestamp, // Example timestamp
        },
      });

      AppLogUtils.debug('Attributes update request sent.');

      // **Verify the attribute was updated**
      const updatedAttributes = await auth.fetchUserAttributes(); // Fetch user attributes
      const updatedAt = updatedAttributes['nickname']; // Access updated attribute

      AppLogUtils.debug('Updated Attribute:', updatedAt);

      if (updatedAt !== timestamp) {
        throw new Error('Failed to update user attributes!');
      }

      AppLogUtils.debug('Attributes successfully updated.');

      // **Force token refresh**
      const session = await fetchAuthSession({ forceRefresh: true });

      // AppLogUtils.info('New ID Token:', session.tokens?.idToken?.toString());
      // AppLogUtils.info('New Access Token:', session.tokens?.accessToken?.toString());

      //Start (or renew) existing subscriptions
      this.notifService.startSubscription();

      return session;
  }

  async updateAvatar(file: File): Promise<string> {
    //TODO UPDATE AVATAR URL IN USER STATE
    
    try {
      const token = await this.getJwtToken();
  
      // Convert to PNG if necessary
      const pngBlob = file.type === 'image/png'
        ? file
        : await this.convertImageToPng(file);
  
      const buffer = await pngBlob.arrayBuffer();
  
      const response = await fetch(`${API_ENDPOINT}/${userApiSchema.uploadAvatar.path}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'image/png',
          'Authorization': `Bearer ${token}`,
        },
        body: buffer,
      });
  
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Upload failed: ${errorText}`);
      }
  
      const data = await response.json();
      console.log('Avatar uploaded:', data);
      return data.url;
    } catch (err) {
      console.error('Error uploading avatar:', err);
      throw err;
    }
  }
  
  async deleteAvatar() {
  
    try {
      const token = await this.getJwtToken();
  
      const response = await fetch(`${API_ENDPOINT}/${userApiSchema.deleteAvatar.path}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });
  
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error removing avatar: ${errorText}`);
      }
  
      const data = await response.json();
      console.log('Avatar removed:', data);
      return data.url;
    } catch (err) {
      console.error('Error removing avatar:', err);
      throw err;
    }
  }
  


  async getJwtToken() {
    try {
      const session = await auth.fetchAuthSession();
      return session.tokens?.idToken;
    } catch (error) {
      console.error('Error fetching JWT token:', error);
      throw error;
    }
  }

  async convertImageToPng(file: File, maxSize = 256): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const image = new Image();
  
      image.onload = () => {
        // Calculate scale to preserve aspect ratio within maxSize
        const scale = Math.min(maxSize / image.width, maxSize / image.height, 1);
        const width = Math.round(image.width * scale);
        const height = Math.round(image.height * scale);
  
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
  
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject('Canvas context not available');
  
        ctx.drawImage(image, 0, 0, width, height);
  
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject('Failed to convert to PNG');
          }
        }, 'image/png');
      };
  
      image.onerror = (err) => reject(err);
  
      const reader = new FileReader();
      reader.onload = (e) => {
        image.src = e.target?.result as string;
      };
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });
  }
  
  

}
